import {
  AlertCircle,
  ChevronDown,
  ChevronRight,
  GripVertical,
  Plus,
  Trash2,
} from 'lucide-react'
import type { ComponentProps, ComponentType } from 'react'
import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { toast } from 'react-hot-toast'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import { TEST_TYPE, type TestType } from '@/lib/constants/test-types'
import { AddTemplateModal } from './add-template-modal'

type MonacoEditorProps = ComponentProps<
  typeof import('@monaco-editor/react')['default']
>

const MonacoEditorLazy = lazy(
  () =>
    import('@monaco-editor/react') as Promise<{
      default: typeof import('@monaco-editor/react')['default']
    }>,
)

function EditorSkeleton() {
  return (
    <div className="h-full w-full space-y-2 p-4">
      <Skeleton className="h-4 w-32" />
      <Skeleton className="h-4 w-24" />
    </div>
  )
}

function MonacoWrapper({
  MonacoComponent,
  isClient,
  monacoProps,
}: {
  MonacoComponent: ComponentType<MonacoEditorProps>
  isClient: boolean
  monacoProps: MonacoEditorProps
}) {
  return (
    <Suspense fallback={<EditorSkeleton />}>
      {isClient ? <MonacoComponent {...monacoProps} /> : <EditorSkeleton />}
    </Suspense>
  )
}

// =============================================================================
// Resizable Code Editor
// =============================================================================

const LINE_HEIGHT_PX = 19 // Monaco default line height
const MIN_EDITOR_HEIGHT = 80
const MAX_EDITOR_HEIGHT = 600
const EDITOR_PADDING = 16 // Top + bottom padding in editor

/**
 * Calculate initial height based on code content (number of lines).
 */
function calculateHeightFromCode(
  code: string | null | undefined,
  minLines = 4,
): number {
  const lines = code ? code.split('\n').length : 0
  const effectiveLines = Math.max(lines + 1, minLines) // +1 for cursor line
  const calculatedHeight = effectiveLines * LINE_HEIGHT_PX + EDITOR_PADDING
  return Math.min(
    Math.max(calculatedHeight, MIN_EDITOR_HEIGHT),
    MAX_EDITOR_HEIGHT,
  )
}

interface ResizableCodeEditorProps {
  code: string | null | undefined
  language: 'python' | 'javascript'
  isDarkMode: boolean
  isClient: boolean
  onChange: (value: string | null) => void
  minLines?: number
}

function ResizableCodeEditor({
  code,
  language,
  isDarkMode,
  isClient,
  onChange,
  minLines = 4,
}: ResizableCodeEditorProps) {
  // Calculate initial height based on content (only on first render)
  // biome-ignore lint/correctness/useExhaustiveDependencies: Intentionally only calculate on mount - recalculating as user types would cause jarring height changes
  const initialHeight = useMemo(
    () => calculateHeightFromCode(code, minLines),
    [],
  )

  return (
    <div
      className="overflow-hidden rounded-md border border-border"
      style={{
        height: initialHeight,
        minHeight: MIN_EDITOR_HEIGHT,
        maxHeight: MAX_EDITOR_HEIGHT,
        resize: 'vertical',
        overflow: 'auto',
      }}
    >
      <MonacoWrapper
        MonacoComponent={MonacoEditorLazy}
        isClient={isClient}
        monacoProps={{
          height: '100%',
          language,
          value: code || '',
          theme: isDarkMode ? 'vs-dark' : 'vs',
          onChange: (val) => onChange(val || null),
          options: {
            automaticLayout: true,
            minimap: { enabled: false },
            scrollBeyondLastLine: false,
            fontSize: 13,
            fontFamily: 'Menlo, Monaco, Consolas, "Courier New", monospace',
            tabSize: 2,
          },
        }}
      />
    </div>
  )
}

// =============================================================================
// Custom hook contracts
// =============================================================================

/**
 * Raw configs have no custom setup/verify hooks — the section is not rendered
 * for them at all. Excluding raw here keeps it out of the contracts; what makes
 * a *future* test type a compile error rather than a hint that quietly states
 * the wrong signature is the `unhandledTestType` guard each contract ends in —
 * `Exclude` on its own would widen silently.
 */
export type HookTestType = Exclude<TestType, typeof TEST_TYPE.RAW>

/**
 * What the runners actually call, per test type and language. Authors get this
 * wrong in ways the editor cannot catch — a class-mode verifier written against
 * the function-mode signature reads the instance as if it were the prepared
 * inputs — so the hint states the real contract rather than one signature for
 * every runner flavor.
 *
 * Kept in step with the custom_setup/custom_verify call sites in
 * PYTHON_CLASS_RUNNER / JS_CLASS_RUNNER and their function-mode counterparts.
 */
interface HookContract {
  signature: string
  /** Each entry explains one argument, in signature order. */
  params: { name: string; meaning: string }[]
  returns: string
  /** A second hook the same editor can define, when the mode has one. */
  note?: string
}

/**
 * Exhaustiveness guard over `HookTestType`. A test type added to `TestType`
 * stops narrowing to `never` here and breaks the build, forcing the new runner
 * flavor to state its own contract instead of inheriting the function-mode one.
 *
 * It returns the fallback rather than throwing like `assertNever`: `testType` is
 * a free-text column, so a value outside the union is reachable at runtime, and
 * white-screening the problem editor over a hint panel is worse than showing the
 * function-mode hint.
 */
function unhandledTestType(
  _testType: never,
  fallback: HookContract,
): HookContract {
  return fallback
}

function setupContract(
  language: 'python' | 'javascript',
  testType: HookTestType,
): HookContract {
  const isPython = language === 'python'
  const name = isPython ? 'custom_setup' : 'customSetup'
  const decl = (args: string) =>
    isPython ? `def ${name}(${args}):` : `function ${name}(${args}) {`

  if (testType === TEST_TYPE.CLASS) {
    const args = isPython ? 'constructor_args, tc' : 'constructorArgs, tc'
    return {
      signature: decl(args),
      params: [
        {
          name: isPython ? 'constructor_args' : 'constructorArgs',
          meaning:
            'the prepared constructor args — markers already decoded into real values',
        },
        { name: 'tc', meaning: 'the whole test case' },
      ],
      returns: 'the arg list to construct the instance with.',
      note: `A class-instance marker in the args is constructed positionally by default. Define ${
        isPython ? 'build_class_instance(data)' : 'buildClassInstance(data)'
      } here when construction needs more than that.`,
    }
  }

  const functionMode: HookContract = {
    signature: decl('inputs, tc'),
    params: [
      {
        name: 'inputs',
        meaning:
          'the prepared inputs — markers already decoded into real values',
      },
      { name: 'tc', meaning: 'the whole test case' },
    ],
    returns: "the prepared inputs to call the user's function with.",
  }
  if (testType === TEST_TYPE.FUNCTION) return functionMode
  return unhandledTestType(testType, functionMode)
}

function verifyContract(
  language: 'python' | 'javascript',
  testType: HookTestType,
): HookContract {
  const isPython = language === 'python'
  const name = isPython ? 'custom_verify' : 'customVerify'
  const nullLiteral = isPython ? 'None' : 'null'
  const opAccess = isPython ? 'op["return_type"]' : 'op.return_type'
  const decl = (args: string) =>
    isPython ? `def ${name}(${args}):` : `function ${name}(${args}) {`
  const returns = `the verdict — ${isPython ? 'True/False' : 'true/false'}.`

  if (testType === TEST_TYPE.CLASS) {
    return {
      signature: decl('got, expected, instance, op'),
      params: [
        {
          name: 'got',
          meaning: `what the method returned — ${nullLiteral} when the method's declared return type is void, so a fluent "return ${isPython ? 'self' : 'this'}" is judged exactly like a bare return. On an operation that checks a property, this is the property's value instead.`,
        },
        {
          name: 'expected',
          meaning: `the operation's expected value, passed through unchanged. A void operation is authored without one, so it is normally ${nullLiteral} — but unlike got, the runner does not normalize it, so test ${opAccess} rather than expected to recognize a void operation. On an operation that checks a property, this is the expected property value instead.`,
        },
        {
          name: 'instance',
          meaning:
            'the live object under test; read it to judge state the return value does not show',
        },
        {
          name: 'op',
          meaning: `the operation being judged. ${opAccess} is the method's declared return type — on an operation that does not check a property, "void" is what makes the runner blank got; an operation that checks one hands you the property's value whatever the method declares, so ${opAccess} alone does not tell you got is ${nullLiteral}. It is ${nullLiteral} on any operation whose method has no declared signature — a config with no methods at all, or one that omits this method — where void-ness is unknowable and nothing is normalized.`,
        },
      ],
      returns,
    }
  }

  const functionMode: HookContract = {
    signature: decl('got, expected, inputs, tc'),
    params: [
      { name: 'got', meaning: "what the user's function returned" },
      { name: 'expected', meaning: "the test case's expected output" },
      {
        name: 'inputs',
        meaning:
          'the prepared inputs, after any custom setup — read them to judge in-place changes',
      },
      { name: 'tc', meaning: 'the whole test case' },
    ],
    returns,
  }
  if (testType === TEST_TYPE.FUNCTION) return functionMode
  return unhandledTestType(testType, functionMode)
}

function HookHint({ contract }: { contract: HookContract }) {
  return (
    <div className="mt-1 space-y-1 text-xs text-muted-foreground">
      <p>
        Define{' '}
        <code className="rounded bg-muted px-1">{contract.signature}</code>
      </p>
      <ul className="ml-1 space-y-0.5">
        {contract.params.map((param) => (
          <li key={param.name}>
            <code className="rounded bg-muted px-1">{param.name}</code> —{' '}
            {param.meaning}
          </li>
        ))}
      </ul>
      <p>Return {contract.returns}</p>
      {contract.note && <p>{contract.note}</p>}
    </div>
  )
}

// Types for templates and custom code
export interface SelectedTemplate {
  id: string
  name: string
  slug: string
  description: string | null
  order: number
}

export interface CustomCodeData {
  setupCode: string | null
  verificationCode: string | null
  testCode: string | null
  testExpectedOutput: string | null
  lastValidatedAt: string | null
}

interface CustomTestLogicSectionProps {
  templates: SelectedTemplate[]
  customCode: Record<string, CustomCodeData>
  onTemplatesChange: (templates: SelectedTemplate[]) => void
  onCustomCodeChange: (customCode: Record<string, CustomCodeData>) => void
  /**
   * Test type of the config being edited — which runner flavor executes it.
   * The custom setup and verify hooks take different arguments in class mode
   * than in function mode, so the hints cannot be written once for both.
   */
  testType: HookTestType
  collisionErrors?: string[]
  /**
   * Set of template IDs that are missing Python definitionCode.
   * Used to show a warning badge next to templates that need attention.
   */
  templatesMissingDefinition?: Set<string>
  /**
   * Whether any parameter or output type is currently using the 'template' type.
   * Used to prevent removing the last template when 'template' type is in use.
   */
  isTemplateTypeInUse?: boolean
}

export function CustomTestLogicSection({
  templates,
  customCode,
  onTemplatesChange,
  onCustomCodeChange,
  testType,
  collisionErrors = [],
  templatesMissingDefinition = new Set(),
  isTemplateTypeInUse = false,
}: CustomTestLogicSectionProps) {
  const [isCollapsed, setIsCollapsed] = useState(true)
  const [showAddModal, setShowAddModal] = useState(false)
  const [activeLanguage, setActiveLanguage] = useState<'python' | 'javascript'>(
    'python',
  )
  const [isClient, setIsClient] = useState(false)
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null)
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null)

  useEffect(() => {
    setIsClient(true)
  }, [])

  const isDarkMode =
    typeof window !== 'undefined'
      ? document.documentElement.classList.contains('dark')
      : false

  const currentCode = customCode[activeLanguage] || {
    setupCode: null,
    verificationCode: null,
    testCode: null,
    testExpectedOutput: null,
    lastValidatedAt: null,
  }

  const updateCustomCode = useCallback(
    (field: keyof CustomCodeData, value: string | null) => {
      onCustomCodeChange({
        ...customCode,
        [activeLanguage]: {
          ...currentCode,
          [field]: value,
        },
      })
    },
    [customCode, activeLanguage, currentCode, onCustomCodeChange],
  )

  // Template management
  const handleRemoveTemplate = (templateId: string) => {
    // Check if this would remove the last template while 'template' type is in use
    const remainingCount = templates.filter((t) => t.id !== templateId).length
    if (remainingCount === 0 && isTemplateTypeInUse) {
      toast.error(
        'Cannot remove last template while a parameter or output uses the "template" type. Change those types first.',
      )
      return
    }
    onTemplatesChange(templates.filter((t) => t.id !== templateId))
  }

  const handleReorderTemplates = (fromIndex: number, toIndex: number) => {
    if (fromIndex === toIndex) return
    const reordered = [...templates]
    const [moved] = reordered.splice(fromIndex, 1)
    reordered.splice(toIndex, 0, moved)
    // Update order values
    const withOrder = reordered.map((t, i) => ({ ...t, order: i }))
    onTemplatesChange(withOrder)
  }

  // Drag handlers
  const handleDragStart = (index: number) => {
    setDraggedIndex(index)
  }

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault()
    if (index !== draggedIndex) {
      setDragOverIndex(index)
    }
  }

  const handleDrop = (toIndex: number) => {
    if (draggedIndex !== null && draggedIndex !== toIndex) {
      handleReorderTemplates(draggedIndex, toIndex)
    }
    setDraggedIndex(null)
    setDragOverIndex(null)
  }

  const handleDragEnd = () => {
    setDraggedIndex(null)
    setDragOverIndex(null)
  }

  // Check if section has any content
  const hasContent =
    templates.length > 0 ||
    Object.values(customCode).some(
      (c) => c?.setupCode || c?.verificationCode || c?.testCode,
    )

  return (
    <div className="space-y-4">
      {/* Collapsible Header */}
      <button
        type="button"
        onClick={() => setIsCollapsed(!isCollapsed)}
        className="flex w-full items-center gap-2 text-left"
      >
        {isCollapsed ? (
          <ChevronRight size={16} className="text-muted-foreground" />
        ) : (
          <ChevronDown size={16} className="text-muted-foreground" />
        )}
        <h3 className="text-sm font-medium">Custom Test Logic (Optional)</h3>
        {hasContent && (
          <span className="text-xs text-muted-foreground">
            {templates.length > 0
              ? `${templates.length} template${templates.length !== 1 ? 's' : ''}`
              : 'Custom code defined'}
          </span>
        )}
      </button>

      {/* Collision Errors */}
      {collisionErrors.length > 0 && (
        <div className="rounded-md border border-destructive/50 bg-destructive/10 p-3">
          <div className="flex items-start gap-2">
            <AlertCircle className="mt-0.5 h-4 w-4 text-destructive" />
            <div className="space-y-1">
              {collisionErrors.map((error, i) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: Errors don't have stable IDs
                <p key={i} className="text-sm text-destructive">
                  {error}
                </p>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Collapsible Content */}
      {!isCollapsed && (
        <div className="space-y-6 rounded-lg border border-border bg-muted/30 p-4">
          {/* Templates Section */}
          <div>
            <div className="mb-2 flex items-center justify-between">
              <label className="text-sm font-medium">Templates</label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setShowAddModal(true)}
              >
                <Plus size={14} className="mr-1" />
                Add Template
              </Button>
            </div>

            {templates.length === 0 ? (
              <div className="rounded-md border border-dashed border-border p-4 text-center">
                <p className="text-sm text-muted-foreground">
                  No templates added. Templates provide reusable code like
                  TreeNode or ListNode class definitions.
                </p>
              </div>
            ) : (
              <div className="space-y-1">
                {templates.map((template, index) => (
                  <div
                    key={template.id}
                    draggable
                    onDragStart={() => handleDragStart(index)}
                    onDragOver={(e) => handleDragOver(e, index)}
                    onDrop={() => handleDrop(index)}
                    onDragEnd={handleDragEnd}
                    className={`flex items-center gap-2 rounded-md border bg-background p-2 ${
                      draggedIndex === index ? 'opacity-50' : ''
                    } ${dragOverIndex === index ? 'border-primary' : 'border-border'}`}
                  >
                    <GripVertical
                      size={14}
                      className="cursor-grab text-muted-foreground active:cursor-grabbing"
                    />
                    <span className="text-xs text-muted-foreground">
                      {index + 1}.
                    </span>
                    <span className="flex-1 text-sm font-medium">
                      {template.name}
                      {templatesMissingDefinition.has(template.id) && (
                        <span className="text-amber-600 bg-amber-50 text-xs px-1.5 py-0.5 rounded ml-2">
                          Missing definition
                        </span>
                      )}
                    </span>
                    <button
                      type="button"
                      onClick={() => handleRemoveTemplate(template.id)}
                      className="rounded p-1 text-muted-foreground hover:text-destructive"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Language Tabs */}
          <div className="flex items-center gap-1 border-b border-border">
            {(['python', 'javascript'] as const).map((lang) => (
              <button
                key={lang}
                type="button"
                onClick={() => setActiveLanguage(lang)}
                className={`px-3 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${
                  activeLanguage === lang
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                {lang === 'python' ? 'Python' : 'JavaScript'}
              </button>
            ))}
          </div>

          {/* Custom Setup Code */}
          <div>
            <label className="mb-1 block text-sm font-medium">
              Custom Setup Code{' '}
              <span className="font-normal text-muted-foreground">
                (optional)
              </span>
            </label>
            <ResizableCodeEditor
              code={currentCode.setupCode}
              language={activeLanguage}
              isDarkMode={isDarkMode}
              isClient={isClient}
              onChange={(val) => updateCustomCode('setupCode', val)}
              minLines={6}
            />
            <HookHint contract={setupContract(activeLanguage, testType)} />
          </div>

          {/* Custom Verification Code */}
          <div>
            <label className="mb-1 block text-sm font-medium">
              Custom Verification Code{' '}
              <span className="font-normal text-muted-foreground">
                (optional)
              </span>
            </label>
            <ResizableCodeEditor
              code={currentCode.verificationCode}
              language={activeLanguage}
              isDarkMode={isDarkMode}
              isClient={isClient}
              onChange={(val) => updateCustomCode('verificationCode', val)}
              minLines={6}
            />
            <HookHint contract={verifyContract(activeLanguage, testType)} />
          </div>

          {/* Validation Test Section */}
          <div className="border-t border-border pt-4">
            <label className="mb-2 block text-sm font-medium">
              Validation Test{' '}
              <span className="font-normal text-muted-foreground">
                (optional)
              </span>
            </label>
            <p className="mb-3 text-xs text-muted-foreground">
              Write test code that validates your custom setup and verification
              logic works correctly. This is run when you save the test
              configuration.
            </p>

            {/* Test Code */}
            <div className="mb-3">
              <label className="mb-1 block text-xs font-medium text-muted-foreground">
                Test Code
              </label>
              <ResizableCodeEditor
                code={currentCode.testCode}
                language={activeLanguage}
                isDarkMode={isDarkMode}
                isClient={isClient}
                onChange={(val) => updateCustomCode('testCode', val)}
                minLines={4}
              />
            </div>

            {/* Expected Output */}
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">
                Expected Output
              </label>
              <Textarea
                value={currentCode.testExpectedOutput || ''}
                onChange={(e) =>
                  updateCustomCode('testExpectedOutput', e.target.value || null)
                }
                placeholder="Expected stdout..."
                className="min-h-[60px] font-mono text-sm"
              />
            </div>
          </div>
        </div>
      )}

      {/* Add Template Modal */}
      <AddTemplateModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onAdd={(selectedTemplates) => {
          // Add selected templates to the list with order values
          const newTemplates = selectedTemplates.map((t, i) => ({
            ...t,
            order: templates.length + i,
          }))
          onTemplatesChange([...templates, ...newTemplates])
          setShowAddModal(false)
        }}
        excludeIds={templates.map((t) => t.id)}
      />
    </div>
  )
}
